import React, { Fragment, useEffect, } from 'react';
import { sumBy, orderBy, chunk, groupBy, get, pick, isEmpty, keyBy, omit, } from 'lodash';
import numeral from 'numeral';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, } from 'reactstrap';
import { toast } from 'react-toastify';
import { addDays, startOfDay, parse as parseDate, isValid as isValidDate, format as formatDate } from 'date-fns';
import { useToggle, useList, } from 'react-use';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';
import dedent from 'dedent';

import firebase, { functions } from '../../firebase';
import { fields, types, statuses, boxSizes, combiningOptions, deliveryTypes, deliveryType, getCombinedItems, computeDocumentNumberForBarcode, } from '../../shared/models/combiningRequest';
import { smallImageUrl, } from '../../shared/models/amazonProduct';
import { idWithTenant, } from '../../shared/util';
import { deliveryTimes, prefectures, } from '../../shared/config';
import { fieldDisplayValue, } from '../../shared/util';
import { newId, fullPathWithParams, getAddressByPostalCode, } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import CombiningRequestFormModal from '../modals/CombiningRequestFormModal';
import AddButton from '../AddButton';
import ImportButton from '../ImportButton';
import ExportButton from '../ExportButton';
import QuerySelector from '../QuerySelector';
import QueryInput from '../QueryInput';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import Pagination from '../Pagination';
import FilesUploadButton from '../FilesUploadButton';
import QuerySortLabel from '../QuerySortLabel';
import CombiningsTabs from '../CombiningsTabs';
import ProductTagsDisplay from '../ProductTagsDisplay';

const countPerPage = 100;
const { values, keys, entries } = Object;
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));
const searchTypes = {
  sku: { label: 'SKU' },
  id: { label: '加工ID' },
};
const searchTypeOptions = entries(searchTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default Page(function CombiningRequests (props) {
  const { tenant, location, history } = props;
  const queryParams = useQueryParams();
  const {
    combiningOptions: combiningOptionsForFilter = [],
    newItems,
    sort = { field: 'id', direction: 'desc', },
    statuses: statusesForFilter = [],
    keyword,
  } = queryParams;
  const searchType = queryParams.searchType?.[0] ?? 'sku';
  const isSearching = !isEmpty(keyword);
  const products = useCollectionSubscription(tenant.ref.collection('products').orderBy('createdAt'), [tenant.id]);
  const productsById = keyBy(products, 'id');
  const productsBySku = keyBy(products, 'sku');
  // 同梱は実物を扱うため、実在庫を持たないセット品は選択肢から除く
  const selectableProducts = products.filter(_ => !_.isSet);
  const combiningRequestsRef = tenant.ref.collection('combiningRequests');
  const combiningRequests = useCollectionSubscription(combiningRequestsRef.orderBy('createdAt', 'desc'), [tenant.id]);

  let filteredCombiningRequests = combiningRequests;
  if(isSearching) {
    filteredCombiningRequests = ({
      sku: _ => filteredCombiningRequests.filter(_ => (_.productIds || []).map(_ => productsById[_]?.sku || '').some(_ => _.includes(keyword))),
      id: _ => filteredCombiningRequests.filter(_ => [_.id].includes(keyword.match(/(C\d+)/)?.[1])),
    })[searchType]();
  } else {
    if(!isEmpty(statusesForFilter)) {
      filteredCombiningRequests = filteredCombiningRequests.filter(_ => statusesForFilter.includes(_.status));
    }
  }
  const pagination = usePagination({ countPerPage, totalCount: filteredCombiningRequests.length, });
  const { offset, } = pagination;
  const pagedCombiningRequests = filteredCombiningRequests.slice(offset, offset + countPerPage);
  const combiningRequestItems = pagedCombiningRequests.map((combiningRequest) => {
    const { combiningItems = [], items = [], senderId, status, } = combiningRequest;
    const itemsWithProduct = items.map((item, i) => {
      const { productId, } = item;
      const product = productsById[productId];
      return { ...item, product };
    }).filter(_ => _.product);
    const isImmutable = ['processing', 'combined'].includes(status);
    return {
      ...combiningRequest,
      itemsWithProduct,
      isImmutable,
    };
  });

  const [lastCombiningRequest] = useCollectionSubscription(combiningRequestsRef.orderBy('createdAt', 'desc').limit(1), [tenant.id]);
  const newItemRef = combiningRequestsRef.doc('C' + newId(lastCombiningRequest));
  const processNewValues = async (values) => {
    return {
      ...values,
      status: 'initial',
      productIds: values.items.map(_ => _.productId),
    };
  };
  useEffect(() => {
    if(isEmpty(statusesForFilter)) {
      const path = fullPathWithParams({ statuses: ['initial', 'waitingToReceive', 'waitingToCombine', 'processing'], }, location);
      history.replace(encodeURI(path));
    }
  }, []);

  return (
    <div className="combining-requests container-fluid">
      <div className="p-4 bg-white my-4">
        <CombiningsTabs activeTab="combiningRequests" />
        <div className="alert alert-danger">
          入荷時にすべての加工が完結する場合は入荷依頼のオプションの「セット」にて依頼して下さい。詳細は<a href="https://www.notion.so/wizzlinx/AmazonFBA-8e29c241308743cbbb8f9cd322e936ab" target="_blank">こちら</a>を参照して下さい。
        </div>
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <div className="card p-2">
            <div style={{ width: 300 }}>
              <QuerySelector className="w-100" paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" disabled={isSearching} />
            </div>
          </div>
          <div className="card p-2 flex-row gap-1">
            <QuerySelector paramName="searchType" label="検索対象" options={searchTypeOptions} defaultValue="sku" />
            <QueryInput paramName="keyword" label="検索キーワード" />
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-end mb-2 gap-1">
          <Pagination {...pagination} />
          <div className="d-flex align-items-center">
            <AddButton label="加工依頼" itemRef={newItemRef} FormModal={CombiningRequestFormModal} initialValues={{}} processValues={processNewValues} formProps={{ tenant, products: selectableProducts, }} />
          </div>
        </div>
        <div className="mt-3 overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            combiningRequestItems.length > 0 ? (
              <table className="table sticky-table table-sm table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th style={{ minWidth: 80 }}>
                      <span field="id">
                        加工ID
                      </span>
                      <br />
                      加工依頼日
                    </th>
                    <th style={{ minWidth: 80 }}>
                      <span field="status">
                        ステータス
                      </span>
                      <br />
                      <span field="completedAt">
                        加工日
                      </span>
                    </th>
                    <th style={{ minWidth: 200 }}>加工元商品</th>
                    <th style={{ minWidth: 50 }}>数量</th>
                    <th style={{ minWidth: 200 }}>完成商品</th>
                    <th style={{ minWidth: 50 }}>完成数量</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    combiningRequestItems.map((combiningRequestItem) => {
                      const { id, ref, status, createdAt, isImmutable, itemsWithProduct, completedAt, } = combiningRequestItem;
                      const rowItems = itemsWithProduct.length > 0 ? itemsWithProduct : [{}];
                      const { color } = statuses[status];
                      const processValues = (values) => {
                        return {
                          ...values,
                          productIds: values.items.map(_ => _.productId),
                        };
                      };
                      const combinedItems = getCombinedItems(combiningRequestItem);

                      return (
                        <Fragment key={id}>
                          {
                            rowItems.map((item, i) => {
                              const { productId, product, } = item;

                              return (
                                <tr key={i} className={`table-${color}`}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          <div>{tenant.id}-{id}</div>
                                          <div className="text-muted">{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div>
                                            {statuses[status].label}
                                          </div>
                                          <div>
                                            {completedAt && formatDate(completedAt.toDate(), 'yyyy/MM/dd')}
                                          </div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    <ProductTagsDisplay product={product} productTags={[]} />
                                    <div className="text-muted small d-flex gap-1">
                                      <div>{product && product.sku}</div>
                                    </div>
                                    <div className="d-flex align-items-start gap-1">
                                      {
                                        product?.isAmazonProduct && (
                                          <div>
                                            <img src={smallImageUrl(product?.amazonProduct)} style={{ maxHeight: 40 }} />
                                          </div>
                                        )
                                      }
                                      <div className="flex-grow-1 text-wrap">
                                        <span>{product?.name}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    {numeral(item.quantity).format('0,0')}
                                  </td>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            combinedItems.map(({ productId }, ci) => {
                                              const combinedProduct = productsById[productId];
                                              return (
                                                <div key={ci} className={ci > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                                  <ProductTagsDisplay product={combinedProduct} productTags={[]} />
                                                  <div className="text-muted small d-flex gap-1">
                                                    <div>{combinedProduct && combinedProduct.sku}</div>
                                                  </div>
                                                  <div className="d-flex align-items-start gap-1">
                                                    <div className="flex-grow-1 text-wrap">
                                                      <span>{combinedProduct?.name}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length} className="text-right">
                                          {
                                            combinedItems.map(({ quantity }, ci) => (
                                              <div key={ci} className={ci > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                                {numeral(quantity).format('0,0')}
                                              </div>
                                            ))
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div className="d-flex flex-column gap-1 text-nowrap">
                                            <EditButton size="sm" itemRef={ref} FormModal={CombiningRequestFormModal} processValues={processValues} formProps={{ tenant, values: combiningRequestItem, products: selectableProducts }} disabled={isImmutable} />
                                            <DeleteButton size="sm" item={combiningRequestItem} itemRef={ref} disabled={isImmutable} />
                                          </div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                加工依頼はありません
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
